import { useState } from "react";
import { Megaphone } from "lucide-react";
import { Modal, Button, Form, Segment } from "semantic-ui-react";
import { useQuery } from "@tanstack/react-query";

import RuvixxForm from "components/RuvixxForm";
import CampaignService from "services/Campaign";
import ContactService from "services/Contact";
import CONSTANTS from "constants/Constants";
import { ModalContactTable } from "components/modals/modal_contact_table/ModalContactTable";
import { useCampaignOptions } from "hooks/useCampaignOptions";
import styles from "./AddToCampaignModal.module.scss";

const AddToCampaignModal = ({
  entityId,
  contactId,
  campaignsAdded = [],
  onSuccess,
}) => {
  const [campaignId, setCampaignId] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [error, setError] = useState(null);
  const [contactIds, setContactIds] = useState([]);
  const [includeAll, setIncludeAll] = useState(false);

  const { data: parentEntityId } = useQuery({
    queryKey: ["contact-parent-entity", "add-to-campaign-modal", contactId],
    queryFn: async () => {
      const response = await ContactService.getContacts({ id: contactId });
      return response.data[0].entity_id;
    },
    enabled: !!contactId && !entityId && modalOpen,
  });

  const resolvedEntityId = entityId || parentEntityId;

  const { campaigns } = useCampaignOptions({
    filters:
      contactId && !entityId && parentEntityId
        ? { entity_id: parentEntityId }
        : null,
    excludeIds: campaignsAdded,
    enabled: modalOpen && !!resolvedEntityId,
  });

  const handleInclude = (contactId, checked) => {
    setContactIds(prev => {
      let next = prev.filter(c => c !== contactId);
      if (checked) {
        next = [...next, contactId];
      } else if (includeAll) {
        next = next.filter(c => c !== -contactId);
        next = [...next, -contactId];
      }
      return next;
    });
  };

  const handleIncludeAll = () => {
    setContactIds([]);
    setIncludeAll(!includeAll);
  };

  const handleSelect = (_, { value }) => {
    setCampaignId(value);
  };

  const handleSubmit = async () => {
    const campaign = campaigns.find(c => c.id === campaignId);
    if (!campaign) return;
    if (campaign.status !== CONSTANTS.CAMPAIGN_STATUS_TYPES.ACTIVE) {
      setError(
        `This campaign is ${
          campaign.status === CONSTANTS.CAMPAIGN_STATUS_TYPES.INACTIVE
            ? "inactive"
            : "archived"
        }`
      );
      return;
    }
    setError(null);

    if (entityId) {
      await CampaignService.createCampaignTarget(
        campaignId,
        parseInt(entityId)
      );
      await CampaignService.addContacts(
        campaignId,
        entityId,
        contactIds,
        includeAll
      );
    } else {
      await CampaignService.addContacts(campaignId, parentEntityId, [
        parseInt(contactId),
      ]);
    }
    handleClose();
  };

  const handleOpen = () => {
    setModalOpen(true);
    setError(null);
  };

  const handleClose = () => {
    setCampaignId(null);
    setContactIds([]);
    setIncludeAll(false);
    setError(null);
    setModalOpen(false);
  };

  const campaignOptions = campaigns.map(({ id, name }) => ({
    key: id,
    text: name,
    value: id,
  }));

  return (
    <Modal
      size="large"
      trigger={
        <Button size="tiny" className="item-adder">
          Add to Campaign
        </Button>
      }
      open={modalOpen}
      onOpen={handleOpen}
      onClose={handleClose}
      closeOnDimmerClick={false}
      closeIcon
    >
      <Modal.Header>Add to Campaign</Modal.Header>
      <Modal.Content>
        <RuvixxForm
          ready={!!campaignId}
          onSubmit={handleSubmit}
          onSuccess={onSuccess}
          error={!!error}
          errorMessage={error}
          submitButtonClassName="item-adder"
        >
          {entityId ? (
            <div className="tw:flex tw:h-[80vh] tw:w-full tw:flex-col tw:overflow-y-auto">
              <ModalContactTable
                entityId={entityId}
                campaignId={campaignId}
                onInclude={handleInclude}
                includeAll={includeAll}
                contactIds={contactIds}
                enableSearch
                headerIcon={Megaphone}
                header="Add to Campaign"
                extraButtons={
                  <div className={styles["extra-buttons"]}>
                    <Form.Select
                      selectOnBlur={false}
                      placeholder="Campaign"
                      options={campaignOptions}
                      onChange={handleSelect}
                      inline
                      search
                    />
                    <Button
                      primary
                      size="tiny"
                      content={
                        includeAll
                          ? "Deselect all contacts"
                          : "Include All Contacts"
                      }
                      onClick={handleIncludeAll}
                    />
                  </div>
                }
              />
            </div>
          ) : (
            <Segment basic className="pad">
              <Form.Select
                selectOnBlur={false}
                inline
                label="Campaign"
                search
                options={campaignOptions}
                onChange={handleSelect}
              />
            </Segment>
          )}
        </RuvixxForm>
      </Modal.Content>
    </Modal>
  );
};

export { AddToCampaignModal };
